<script setup>
import BasePage from "@/components/BasePage.vue";
import { Card } from "@/components/atomic";
import {api} from "@/services/api.js";
import {ref, onMounted, onUnmounted, computed } from 'vue'
import { useWeekStore } from '@/stores/week.js'

const weekStore = useWeekStore()
import {useRouter} from 'vue-router'

const router = useRouter()

const activeWeeks = ref([])
const isRefreshing = ref(false)
const startY = ref(0)
const pullDistance = ref(0)
const maxPullDistance = 150
const selectedWeek = computed(() => weekStore.selectedWeek)
const showModal = computed(() => weekStore.isLoading)

const selectWeek = async (week) => {
  if(await weekStore.weekId !== week.weekId || !weekStore.weekData || weekStore.weekData.length < 1) {
    await weekStore.loadWeekData(week)
  }
  await router.push('/hub')
}

const getWeeks = async () => {
  isRefreshing.value = true
  try {
    const response = await api.get("/api/week/active")
    activeWeeks.value = response.activeWeeks.sort((a, b) => {
      const dateA = JSON.parse(a.weekPackages)[0].departureDate;
      const dateB = JSON.parse(b.weekPackages)[0].departureDate;
      return new Date(dateA) - new Date(dateB);
    })
  } catch (error) {
    console.error("Failed to fetch weeks:", error)
  } finally {
    isRefreshing.value = false
    pullDistance.value = 0
  }
}

const handleTouchStart = (e) => {
  if (window.scrollY === 0) {
    startY.value = e.touches[0].clientY
  }
}

const handleTouchMove = (e) => {
  if (startY.value && window.scrollY === 0) {
    const currentY = e.touches[0].clientY
    pullDistance.value = Math.max(0, Math.min(currentY - startY.value, maxPullDistance))
  }
}

const handleTouchEnd = () => {
  if (pullDistance.value >= maxPullDistance) {
    getWeeks()
  }
  startY.value = 0
  if (!isRefreshing.value) {
    pullDistance.value = 0
  }
}

onMounted(() => {
  getWeeks()
  document.addEventListener('touchstart', handleTouchStart)
  document.addEventListener('touchmove', handleTouchMove)
  document.addEventListener('touchend', handleTouchEnd)
})

onUnmounted(() => {
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
})
</script>

<template>
  <BasePage pageTitle="Select Week" show-menu>
    <div class="pull-to-refresh" :style="{ transform: `translateY(${pullDistance}px)` }">
      <div v-if="isRefreshing" class="pull-down-indicator" :class="{ 'refreshing': isRefreshing }">
        {{ isRefreshing ? __DICT__.refreshing : __DICT__.pullDownToRefresh }}
      </div>
      <Card margin="">
        <div class="list-group list-group-flush">
          <button @click.prevent="selectWeek(week)" v-for="week in activeWeeks" :key="week.id"
             class="list-group-item list-group-item-action" :class="{ 'alwaysOn': week.alwaysOn, 'disabled': week.disabled }">
            {{ week.weekName }}</button>
        </div>
      </Card>
    </div>
    <nav class="navbar bg-body fixed-bottom navbar-expand justify-content-center">
      <span class="pt-2">{{ __DICT__.pullDownToRefresh }}</span>
    </nav>
  </BasePage>
  <div class="modal modal-blur fade" :class="{ 'show': showModal }" tabindex="-1"
       :style="{ display: showModal ? 'block' : 'none' }" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-sm modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="text-center">
            <div class="spinner-border text-primary" role="status"></div>
            <div class="mt-2">{{ __DICT__.loading }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showModal" class="modal-backdrop fade show"></div>
</template>

<style scoped>
.pull-to-refresh {
  transition: transform 0.2s;
}

.alwaysOn {
  border-left-color: var(--tblr-success);
  border-left-width: 2px;
}

.disabled {
  border-left-color: var(--tblr-warning);
  border-left-width: 2px;
}

.pull-down-indicator {
  text-align: center;
  padding: 10px;
  background-color: var(--tblr-bg-surface-secondary);
  border-bottom: 1px solid var(--tblr-border-color);
}

.refreshing {
  color: var(--tblr-primary);
}
</style>
